import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Trash2, X, Download, Award, Ticket, DollarSign, Play, Image as ImageIcon, FileText, ExternalLink, QrCode, Pencil, FileCheck2, LockKeyhole, History, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { API } from "../../lib/api";
import { WinnerPosterModal } from "./WinnerPosterModal";
import { RaffleDigestPanel } from "./RaffleDigestPanel";
import { RaffleForm } from "./RaffleForm";
import { RaffleHistory } from "./RaffleHistory";
import { useAuth } from "../../context/AuthContext";
import { OfflinePaymentsPanel } from "./OfflinePaymentsPanel";
import { RaffleBuyerTickets } from "./RaffleBuyerTickets";
import { RaffleTicketReminders } from "./RaffleTicketReminders";

const PAYMENT_LABELS = {
    cash: "Cash",
    check: "Check",
    card: "Card",
};

const STATUS_BADGE = {
    draft: "bg-[#F4F1EA] text-[#4A5568]",
    open: "bg-green-100 text-green-800",
    closed: "bg-red-50 text-[#E53935]",
};

const RafflesPanel = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [rows, setRows] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState("");
    const [adding, setAdding] = useState(false);
    const [active, setActive] = useState(null); // raffle currently being inspected
    const [entries, setEntries] = useState([]);
    const [entriesView, setEntriesView] = useState("tickets");
    const [entriesLoading, setEntriesLoading] = useState(false);
    const [poster, setPoster] = useState(null);
    const [drawing, setDrawing] = useState(false);
    const [outreach, setOutreach] = useState(null);  // { attempts, deadline_at, days_remaining, winner_public }
    const [outreachForm, setOutreachForm] = useState({ method: "phone", reached: false, notes: "" });
    const [editing, setEditing] = useState(null);
    const [history, setHistory] = useState(null);

    const load = async () => {
        setLoading(true);
        setLoadError("");
        try {
            const [r, c] = await Promise.all([
                api.get("/admin/raffles"),
                api.get("/campaigns").catch(() => ({ data: [] })),
            ]);
            setRows(r.data || []);
            setCampaigns((c.data || []).filter((x) => x.active !== false));
        } catch (e) {
            setRows([]);
            setLoadError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Could not load raffles.");
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    const closeForm = () => { setAdding(false); setEditing(null); };
    const saveRaffle = async (payload) => {
        if (editing) await api.patch(`/admin/raffles/${editing.id}`, payload);
        else await api.post("/admin/raffles", payload);
        toast.success(editing ? "Raffle changes saved." : "Draft created.");
        closeForm();
        await load();
    };

    const beginEdit = (r) => {
        if (!r.permissions?.edit) {
            toast.error("This raffle is locked for your account.");
            return;
        }
        setAdding(false);
        setEditing(r);
    };

    const setStatus = async (r, status) => {
        try {
            await api.patch(`/admin/raffles/${r.id}`, { status, expected_version: r.edit_version });
            toast.success(`Raffle ${status}`);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Update failed");
        }
    };

    const remove = async (r) => {
        const isDrawn = !!r.drawn_at;
        const msg = isDrawn
            ? `"${r.name}" has already been DRAWN. Force-deleting will wipe entries, linked donations, and journal entries — cascade. Only do this for test raffles. Continue?`
            : `Delete raffle "${r.name}" and every linked entry, donation, and journal entry? This cannot be undone.`;
        if (!window.confirm(msg)) return;
        try {
            const { data } = await api.delete(`/admin/raffles/${r.id}${isDrawn ? "?force=true" : ""}`);
            const c = data?.cascade || {};
            toast.success(
                `Raffle deleted · ${c.entries_deleted || 0} entries, ${c.donations_deleted || 0} donations, ${c.journal_entries_deleted || 0} JEs removed`
            );
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Delete failed");
        }
    };

    const openEntries = async (r) => {
        setActive(r);
        setEntriesLoading(true);
        setEntries([]);
        setOutreach(null);
        try {
            const { data } = await api.get(`/admin/raffles/${r.id}/entries`);
            setEntries(data || []);
        } catch (e) {
            toast.error("Could not load entries");
        } finally {
            setEntriesLoading(false);
        }
        if (r.drawn_at) {
            loadOutreach(r.id);
        }
    };

    const loadOutreach = async (raffleId) => {
        try {
            const { data } = await api.get(`/admin/raffles/${raffleId}/outreach`);
            setOutreach(data);
        } catch {
            /* silent — outreach is a soft feature */
        }
    };

    const logOutreach = async () => {
        try {
            await api.post(`/admin/raffles/${active.id}/outreach`, outreachForm);
            toast.success(`Logged ${outreachForm.method} attempt`);
            setOutreachForm({ method: outreachForm.method, reached: false, notes: "" });
            loadOutreach(active.id);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Log failed");
        }
    };

    const deleteOutreach = async (attemptId) => {
        if (!window.confirm("Delete this outreach entry?")) return;
        try {
            await api.delete(`/admin/raffles/${active.id}/outreach/${attemptId}`);
            loadOutreach(active.id);
        } catch (e) {
            toast.error("Delete failed");
        }
    };

    const markPaid = async (entry) => {
        const ref = window.prompt(`Payment reference for ${entry.entrant_name} (check #, receipt, etc.)?`, entry.payment_reference || "");
        if (ref === null) return;
        try {
            const { data } = await api.patch(`/admin/raffles/${active.id}/entries/${entry.id}/pay`, { payment_reference: ref });
            toast.success(`Paid. Tickets ${data.ticket_numbers.join(", ")}`);
            await openEntries(active);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Mark-paid failed");
        }
    };

    const draw = async () => {
        if (!window.confirm("Draw a winner now? This action is final and recorded for compliance.")) return;
        setDrawing(true);
        try {
            const { data } = await api.post(`/admin/raffles/${active.id}/draw`);
            toast.success(`Winner drawn — ticket #${data.winning_ticket_number}`);
            setPoster({ ...active, drawn_at: data.drawn_at, winner_poster_status: data.poster?.status, winner_poster_version: data.poster?.version });
            await openEntries(active);
            await load();
            // refresh `active` from latest rows
            const fresh = (await api.get("/admin/raffles")).data || [];
            const me = fresh.find((x) => x.id === active.id);
            if (me) { setActive(me); loadOutreach(me.id); }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Draw failed");
        } finally {
            setDrawing(false);
        }
    };

    const redraw = async () => {
        const days = active.claim_deadline_days || 30;
        const proceed = window.confirm(
            `Redraw a new winner?\n\n` +
            `The previous winner will be permanently excluded. A new pick will be made from the remaining paid tickets using the same secure random selection.\n\n` +
            `You should only do this after the ${days}-day claim window has passed AND you've documented outreach (phone, text, email). ` +
            `If the deadline hasn't passed, the backend will block you — check 'Override' if the winner declined in writing.`
        );
        if (!proceed) return;
        const override = window.confirm(
            `Override the ${days}-day claim deadline?\n\n` +
            `Click OK only if the winner formally declined in writing OR the deadline has already passed. ` +
            `Click Cancel to enforce the deadline (recommended).`
        );
        setDrawing(true);
        try {
            const { data } = await api.post(`/admin/raffles/${active.id}/redraw`, {
                reason: "unclaimed",
                override_deadline: override,
            });
            toast.success(`New winner — ticket #${data.winning_ticket_number} (pool ${data.pool_size}, excluded ${data.excluded_count})`);
            setPoster({ ...active, drawn_at: data.drawn_at, winner_poster_status: data.poster?.status, winner_poster_version: data.poster?.version });
            await openEntries(active);
            await load();
            const fresh = (await api.get("/admin/raffles")).data || [];
            const me = fresh.find((x) => x.id === active.id);
            if (me) { setActive(me); loadOutreach(me.id); }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Redraw failed");
        } finally {
            setDrawing(false);
        }
    };

    const downloadTicketPdf = async (entry) => {
        const token = localStorage.getItem("token");
        try {
            const res = await fetch(`${API}/admin/raffles/${active.id}/entries/${entry.id}/ticket.pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("Download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `raffle-ticket-${active.slug}-${entry.id.slice(0, 8)}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch {
            toast.error("Ticket PDF download failed");
        }
    };

    const downloadRaffleQr = async (r) => {
        // Public endpoint — no auth needed. Fetch as blob so it downloads
        // instead of opening inline in a new tab.
        try {
            const res = await fetch(`${API}/raffles/${r.slug}/qr.png?size=1024`);
            if (!res.ok) throw new Error("QR download failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `raffle-qr-${r.slug}.png`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("QR downloaded — drop it on a flyer!");
        } catch {
            toast.error("Could not download QR");
        }
    };

    const previewRaffle = (r) => {
        // Opens the public raffle page in preview mode via ?preview=1 so
        // draft raffles are visible (normal /raffles/{slug} 404s on draft).
        // Existing authenticated cookies authorize this private preview.
        window.open(`/raffles/${r.slug}?preview=1`, "_blank", "noopener,noreferrer");
    };

    const downloadCompliancePdf = async (r) => {
        // Filing-ready cover sheet for MT DOJ Gambling Control Division.
        // Pulls fresh numbers so re-running after new sales stays accurate.
        const token = localStorage.getItem("token");
        try {
            const res = await fetch(`${API}/admin/raffles/${r.id}/compliance.pdf`, {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            });
            if (!res.ok) throw new Error("compliance pdf failed");
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `raffle-${r.slug}-compliance.pdf`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success("Compliance sheet ready — sign and file with your Form 46.");
        } catch {
            toast.error("Could not generate compliance PDF");
        }
    };

    const downloadCsv = () => {
        const token = localStorage.getItem("token");
        // Open in new tab — auth header is via cookie; if not, fall back to fetch+blob.
        if (token) {
            fetch(`${API}/admin/raffles/${active.id}/report.csv`, { headers: { Authorization: `Bearer ${token}` } })
                .then((r) => r.blob())
                .then((blob) => {
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `raffle-${active.slug}-report.csv`;
                    a.click();
                    URL.revokeObjectURL(url);
                })
                .catch(() => toast.error("Download failed"));
        } else {
            window.open(`${API}/admin/raffles/${active.id}/report.csv`, "_blank");
        }
    };

    return (
        <div className="space-y-6 [overflow-wrap:anywhere]" data-testid="raffles-panel">
            <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
                <div>
                    <h2 className="font-heading text-2xl text-[#1C252E] tracking-tight">Charitable raffles</h2>
                    <p className="text-sm text-[#4A5568] mt-1 max-w-2xl">
                        Montana compliance (HB141, 2023): <strong>credit + debit cards, cash and checks</strong> are all accepted. Entries stay pending until you mark them paid (cash/check) or the online capture confirms (Stripe/PayPal). File <strong>Form 46</strong> with the MT DOJ Gambling Control Division <em>before</em> opening online sales. Records retained 12 months after draw.
                    </p>
                </div>
                <button
                    onClick={() => { setEditing(null); setAdding(true); }}
                    disabled={loading || Boolean(loadError) || adding || Boolean(editing)}
                    data-testid="raffle-add-btn"
                    className="inline-flex items-center gap-2 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-5 py-2.5 text-sm font-medium transition-colors disabled:opacity-50"
                >
                    <Plus className="w-4 h-4" strokeWidth={2} /> New raffle
                </button>
            </div>

            {!isAdmin && <p data-testid="raffle-board-permissions-note" className="text-sm text-[#4A5568] flex items-start gap-2"><LockKeyhole size={16} className="shrink-0 mt-0.5" />Your drafts are editable until publication. Publishing, live changes and winner draws are admin-only.</p>}
            {isAdmin && <RaffleDigestPanel />}
            {isAdmin && <OfflinePaymentsPanel kind="raffle" onConfirmed={() => { load(); if (active) openEntries(active); }} />}
            {isAdmin && poster && <WinnerPosterModal key={`${poster.id}-${poster.drawn_at}`} raffle={poster} onClose={() => { setPoster(null); load(); }} />}
            {isAdmin && history && <RaffleHistory raffle={history} onClose={() => setHistory(null)} />}

            {(adding || editing) && <RaffleForm key={editing?.id || "new"} raffle={editing} campaigns={campaigns} onSubmit={saveRaffle} onCancel={closeForm} />}

            {loadError ? <div data-testid="raffles-load-error" role="alert" className="text-sm text-red-700 space-y-2"><p>{loadError}</p><div className="flex gap-4"><button data-testid="raffles-retry-load" onClick={load} className="underline hover:text-red-900 transition-colors">Retry</button><Link data-testid="raffles-account-settings" to="/account" className="underline hover:text-red-900 transition-colors">Account settings</Link></div></div> : loading ? (
                <p data-testid="raffles-loading" className="text-sm text-[#4A5568]">Loading…</p>
            ) : rows.length === 0 ? (
                <p className="text-sm text-[#4A5568] italic" data-testid="raffles-empty">No raffles yet.</p>
            ) : (
                <ul className="space-y-3" data-testid="raffles-list">
                    {rows.map((r) => (
                        <li key={r.id} data-testid={`raffle-row-${r.id}`} className="bg-white border border-black/10 rounded-2xl p-4">
                            <div className="flex items-start justify-between gap-3 flex-wrap">
                                {r.prize_image_url ? (
                                    <img
                                        src={r.prize_image_url}
                                        alt=""
                                        data-testid={`raffle-row-image-${r.id}`}
                                        className="w-16 h-16 rounded-xl object-cover flex-shrink-0 bg-[#F4F1EA]"
                                        loading="lazy"
                                    />
                                ) : null}
                                <div className="min-w-0 flex-1">
                                    <p data-testid={`raffle-name-${r.id}`} className="font-medium text-[#1C252E]">{r.name}</p>
                                    <p className="text-xs text-[#4A5568] mt-0.5"><Award className="inline w-3 h-3 -mt-0.5" /> {r.prize_description}</p>
                                    <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] uppercase tracking-wider font-bold">
                                        <span data-testid={`raffle-status-${r.id}`} className={`px-2 py-0.5 rounded-full ${STATUS_BADGE[r.status] || STATUS_BADGE.draft}`}>{r.status}</span>
                                        <span data-testid={`raffle-price-${r.id}`} className="px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#4A5568]">${r.ticket_price_usd}/ticket</span>
                                        <span data-testid={`raffle-totals-${r.id}`} className="px-2 py-0.5 rounded-full bg-[#F4F1EA] text-[#4A5568]">{r.total_tickets_sold || 0} sold · ${(r.total_proceeds_usd || 0).toFixed(2)}</span>
                                        {!isAdmin && !r.permissions?.edit && <span data-testid={`raffle-locked-${r.id}`} className="inline-flex items-center gap-1 text-[#4A5568]"><LockKeyhole size={12} />Read-only</span>}
                                        <span
                                            data-testid={`raffle-fund-destination-${r.id}`}
                                            className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200"
                                            title="Where the paid ticket proceeds go"
                                        >
                                            Funds → {r.campaign_name || "General Support Fund"}
                                        </span>
                                        {r.drawn_at && <span className="px-2 py-0.5 rounded-full bg-amber-50 text-amber-800">Winner drawn</span>}
                                    </div>
                                </div>
                                <div className="flex items-center gap-1 flex-wrap justify-end">
                                    {isAdmin && r.drawn_at && <button onClick={() => setPoster(r)} data-testid={`raffle-poster-${r.id}`} className="p-1.5 rounded-full text-[#E53935] hover:bg-[#F4F1EA]" aria-label="Winner poster" title="Winner poster — share or print"><ImageIcon className="w-4 h-4" strokeWidth={1.5} /></button>}
                                    <button
                                        onClick={() => previewRaffle(r)}
                                        data-testid={`raffle-preview-${r.id}`}
                                        className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] hover:text-[#1C252E]"
                                        aria-label="Preview public raffle page"
                                        title="Preview what buyers see"
                                    >
                                        <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    <button
                                        onClick={() => downloadRaffleQr(r)}
                                        data-testid={`raffle-qr-${r.id}`}
                                        className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] hover:text-[#1C252E]"
                                        aria-label="Download raffle QR code for flyers"
                                        title="Download QR for flyers"
                                    >
                                        <QrCode className="w-4 h-4" strokeWidth={1.5} />
                                    </button>
                                    {isAdmin && <button
                                        onClick={() => downloadCompliancePdf(r)}
                                        data-testid={`raffle-compliance-${r.id}`}
                                        className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] hover:text-[#1C252E]"
                                        aria-label="Download filing-ready compliance PDF for MT DOJ"
                                        title="Download compliance PDF (Form 46 cover sheet)"
                                    >
                                        <FileCheck2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>}
                                    {isAdmin && <button data-testid={`raffle-history-${r.id}`} onClick={() => setHistory(r)} aria-label="Change history" title="Change history" className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]"><History size={16} /></button>}
                                    {r.permissions?.edit && (
                                        <button
                                            onClick={() => beginEdit(r)}
                                            disabled={adding || Boolean(editing)}
                                            data-testid={`raffle-edit-${r.id}`}
                                            className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA] hover:text-[#1C252E] disabled:opacity-50"
                                            aria-label={r.published_once ? "Edit live raffle" : "Edit draft raffle"}
                                            title={r.published_once ? "Admin edit — recorded in change history" : "Edit draft raffle"}
                                        >
                                            <Pencil className="w-4 h-4" strokeWidth={1.5} />
                                        </button>
                                    )}
                                    {isAdmin && <button onClick={() => openEntries(r)} data-testid={`raffle-entries-${r.id}`} className="p-1.5 rounded-full text-[#4A5568] hover:bg-[#F4F1EA]" aria-label="Manage entries">
                                        <span className="inline-flex items-center gap-1 text-xs"><Ticket className="w-4 h-4" strokeWidth={1.5} />Entries</span>
                                    </button>}
                                    {isAdmin && (r.status === "draft" || r.status === "closed") && !r.drawn_at && (
                                        <button onClick={() => setStatus(r, "open")} data-testid={`raffle-open-${r.id}`} className="inline-flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white rounded-full px-3 py-1 font-medium" aria-label="Open">
                                            {r.status === "closed" ? "Reopen sales" : "Open"}
                                        </button>
                                    )}
                                    {isAdmin && r.status === "open" && !r.drawn_at && (
                                        <button onClick={() => setStatus(r, "closed")} data-testid={`raffle-close-${r.id}`} className="inline-flex items-center gap-1 text-xs bg-[#1C252E] hover:bg-black text-white rounded-full px-3 py-1 font-medium" aria-label="Close">
                                            Close sales
                                        </button>
                                    )}
                                    {isAdmin && <button
                                        onClick={() => remove(r)}
                                        data-testid={`raffle-delete-${r.id}`}
                                        className="p-1.5 rounded-full text-[#4A5568] hover:text-[#E53935] hover:bg-[#E53935]/10"
                                        aria-label={r.drawn_at ? "Force-delete drawn raffle (test only)" : "Delete raffle"}
                                        title={r.drawn_at ? "Force-delete — wipes entries + donations + JEs. Use only for test raffles." : "Delete raffle and every linked entry / donation / JE"}
                                    >
                                        <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                                    </button>}
                                </div>
                            </div>
                            {r.winner_entry_public && (
                                <div className="mt-2 text-xs bg-amber-50 border border-amber-200 rounded-xl p-2 text-amber-900">
                                    <strong>Winner:</strong> {r.winner_entry_public.first_name} {r.winner_entry_public.last_initial} — ticket #{r.winner_entry_public.winning_ticket_number}
                                </div>
                            )}
                        </li>
                    ))}
                </ul>
            )}

            {isAdmin && active && (
                <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-4" data-testid="raffle-entries-modal">
                    <div className="bg-white rounded-2xl max-w-5xl w-full max-h-[88vh] flex flex-col">
                        <div className="flex items-center justify-between flex-wrap gap-3 p-5 border-b border-black/5">
                            <div className="min-w-0">
                                <p className="text-xs uppercase tracking-wider font-bold text-[#E53935]">Raffle entries</p>
                                <h3 className="font-heading text-lg text-[#1C252E] truncate">{active.name}</h3>
                                <p data-testid="raffle-entry-payment-summary" className="text-xs text-[#4A5568] mt-0.5">{entries.filter(e => e.paid).reduce((sum, e) => sum + (e.ticket_numbers?.length || 0), 0)} paid tickets · {entries.filter(e => !e.paid && !e.cancelled_at).length} awaiting payment · {entries.filter(e => e.cancelled_at).length} cancelled</p>
                            </div>
                            <div className="flex items-center flex-wrap gap-1">
                                <button data-testid="raffle-entries-refresh" onClick={() => openEntries(active)} disabled={entriesLoading} aria-label="Refresh entries" title="Refresh entries" className="p-2 rounded-full hover:bg-[#F4F1EA] disabled:opacity-50"><RefreshCw size={16} /></button>
                                {active.drawn_at && <button onClick={() => setPoster(active)} data-testid="raffle-active-poster" aria-label="Winner poster" title="Winner poster" className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#E53935]"><ImageIcon size={18} /></button>}
                                <button onClick={downloadCsv} data-testid="raffle-csv-btn" className="p-2 rounded-full hover:bg-[#F4F1EA] text-[#4A5568]" aria-label="Download CSV">
                                    <Download className="w-4 h-4" strokeWidth={1.5} />
                                </button>
                                {!active.drawn_at && (
                                    <button onClick={draw} disabled={drawing} data-testid="raffle-draw-btn" className="inline-flex items-center gap-1 text-xs bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1.5 font-medium disabled:opacity-50">
                                        <Play className="w-3.5 h-3.5" strokeWidth={2} /> {drawing ? "Drawing…" : "Draw winner"}
                                    </button>
                                )}
                                {active.drawn_at && (
                                    <button onClick={redraw} disabled={drawing} data-testid="raffle-redraw-btn" title={`Redraw a new winner (requires ${active.claim_deadline_days || 30}-day claim window elapsed, or override)`} className="inline-flex items-center gap-1 text-xs bg-white border border-[#E53935] text-[#E53935] hover:bg-[#FEF2F2] rounded-full px-3 py-1.5 font-medium disabled:opacity-50">
                                        <Play className="w-3.5 h-3.5" strokeWidth={2} /> Redraw (unclaimed)
                                    </button>
                                )}
                                <button data-testid="raffle-entries-close" onClick={() => { setActive(null); setEntries([]); }} className="p-2 rounded-full hover:bg-[#F4F1EA]" aria-label="Close">
                                    <X className="w-4 h-4 text-[#4A5568]" strokeWidth={1.5} />
                                </button>
                            </div>
                        </div>
                        <div className="flex-1 overflow-y-auto p-5">
                            {outreach && outreach.winner_entry_id && (
                                <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50/70 p-4" data-testid="raffle-outreach-panel">
                                    <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
                                        <div>
                                            <p className="text-[10px] uppercase tracking-widest font-bold text-amber-800">Winner outreach log</p>
                                            <p className="text-sm text-[#1C252E] font-medium mt-1">Winner: {outreach.winner_public?.first_name} {outreach.winner_public?.last_initial} · ticket #{outreach.winner_public?.winning_ticket_number}</p>
                                        </div>
                                        {typeof outreach.days_remaining === "number" && (
                                            <div className={`text-xs font-medium px-3 py-1 rounded-full ${outreach.days_remaining < 0 ? "bg-red-100 text-red-800" : outreach.days_remaining < 7 ? "bg-orange-100 text-orange-800" : "bg-white text-[#4A5568] border border-black/10"}`}>
                                                {outreach.days_remaining < 0
                                                    ? `Deadline passed ${Math.abs(outreach.days_remaining)}d ago — you can redraw`
                                                    : `${outreach.days_remaining} day${outreach.days_remaining === 1 ? "" : "s"} left in claim window`}
                                            </div>
                                        )}
                                    </div>
                                    {(outreach.attempts || []).length > 0 ? (
                                        <ul className="space-y-1.5 mb-3">
                                            {outreach.attempts.map((a) => (
                                                <li key={a.id} className="flex items-start justify-between gap-3 text-xs bg-white rounded-lg border border-black/5 px-3 py-2" data-testid={`outreach-attempt-${a.id}`}>
                                                    <div className="flex items-start gap-3">
                                                        <span className={`inline-block w-2 h-2 mt-1.5 rounded-full ${a.reached ? "bg-green-600" : "bg-orange-400"}`} />
                                                        <div>
                                                            <p className="text-[#1C252E]">
                                                                <span className="font-semibold uppercase tracking-wider text-[10px]">{a.method}</span>
                                                                <span className="text-[#4A5568] ml-2">{a.reached ? "· reached" : "· no answer"}</span>
                                                            </p>
                                                            {a.notes && <p className="text-[#4A5568] mt-0.5">{a.notes}</p>}
                                                            <p className="text-[10px] text-[#9CA3AF] mt-0.5">{(a.logged_at || "").split("T")[0]} · {a.logged_by_email || "admin"}</p>
                                                        </div>
                                                    </div>
                                                    <button onClick={() => deleteOutreach(a.id)} className="text-[10px] uppercase text-[#9CA3AF] hover:text-[#E53935]" data-testid={`outreach-delete-${a.id}`}>Delete</button>
                                                </li>
                                            ))}
                                        </ul>
                                    ) : (
                                        <p className="text-xs text-[#4A5568] mb-3 italic">No outreach logged yet. Try phone → text → email in that order.</p>
                                    )}
                                    <div className="flex flex-wrap items-end gap-2 pt-3 border-t border-amber-200/70">
                                        <div className="flex flex-col">
                                            <label className="text-[10px] uppercase tracking-widest font-bold text-amber-800 mb-1">Method</label>
                                            <select value={outreachForm.method} onChange={(e) => setOutreachForm({ ...outreachForm, method: e.target.value })} data-testid="outreach-method-select" className="text-xs rounded-lg border border-black/15 bg-white px-2.5 py-1.5">
                                                <option value="phone">Phone call</option>
                                                <option value="text">Text (SMS)</option>
                                                <option value="email">Email</option>
                                                <option value="mail">Mail</option>
                                                <option value="in_person">In person</option>
                                            </select>
                                        </div>
                                        <label className="flex items-center gap-2 text-xs text-[#1C252E] cursor-pointer">
                                            <input type="checkbox" checked={outreachForm.reached} onChange={(e) => setOutreachForm({ ...outreachForm, reached: e.target.checked })} data-testid="outreach-reached-checkbox" className="w-3.5 h-3.5 accent-[#E53935]" />
                                            Reached them
                                        </label>
                                        <input type="text" value={outreachForm.notes} onChange={(e) => setOutreachForm({ ...outreachForm, notes: e.target.value })} placeholder="Notes (voicemail left, busy, etc.)" data-testid="outreach-notes-input" className="flex-1 min-w-[200px] text-xs rounded-lg border border-black/15 bg-white px-2.5 py-1.5" />
                                        <button onClick={logOutreach} data-testid="outreach-log-btn" className="inline-flex items-center gap-1 text-xs bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1.5 font-medium">
                                            Log attempt
                                        </button>
                                    </div>
                                </div>
                            )}
                            <RaffleTicketReminders key={active.id} raffleId={active.id} />
                            <div role="tablist" aria-label="Raffle entries view" className="flex flex-wrap gap-2 mb-4"><button role="tab" aria-selected={entriesView === "tickets"} data-testid="raffle-view-buyer-tickets" onClick={() => setEntriesView("tickets")} className={`rounded-full px-4 py-2 text-sm transition-colors ${entriesView === "tickets" ? "bg-[#1C252E] text-white" : "bg-[#F4F1EA] text-[#1C252E]"}`}>Tickets by person</button><button role="tab" aria-selected={entriesView === "entries"} data-testid="raffle-view-entry-payments" onClick={() => setEntriesView("entries")} className={`rounded-full px-4 py-2 text-sm transition-colors ${entriesView === "entries" ? "bg-[#1C252E] text-white" : "bg-[#F4F1EA] text-[#1C252E]"}`}>Entries &amp; payments</button></div>
                            {entriesLoading ? (
                                <p className="text-sm text-[#4A5568]">Loading…</p>
                            ) : entriesView === "tickets" ? <RaffleBuyerTickets entries={entries} /> : entries.length === 0 ? (
                                <p className="text-sm text-[#4A5568] italic">No entries yet.</p>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="w-full text-sm">
                                        <thead className="text-left text-[10px] uppercase tracking-wider font-bold text-[#4A5568]">
                                            <tr>
                                                <th className="py-2 pr-3">Created</th>
                                                <th className="py-2 pr-3">Name</th>
                                                <th className="py-2 pr-3">Contact</th>
                                                <th className="py-2 pr-3">Tickets</th>
                                                <th className="py-2 pr-3">Due</th>
                                                <th className="py-2 pr-3">Method</th>
                                                <th className="py-2 pr-3">Paid?</th>
                                                <th className="py-2 pr-3">Ticket #s</th>
                                                <th className="py-2 pr-3"></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {entries.map((e) => (
                                                <tr key={e.id} className="border-t border-black/5">
                                                    <td className="py-2 pr-3 text-xs text-[#4A5568] whitespace-nowrap">{(e.created_at || "").slice(0, 16).replace("T", " ")}</td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{e.entrant_name}</td>
                                                    <td className="py-2 pr-3 text-[#4A5568] text-xs">
                                                        <div>{e.entrant_email}</div>
                                                        <div>{e.entrant_phone}</div>
                                                    </td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">{e.ticket_count}</td>
                                                    <td className="py-2 pr-3 text-[#1C252E]">${(e.amount_due_usd || 0).toFixed(2)}</td>
                                                    <td className="py-2 pr-3 text-[#4A5568]">{PAYMENT_LABELS[e.payment_method] || e.payment_method}</td>
                                                    <td className="py-2 pr-3">
                                                        {e.cancelled_at ? <span data-testid={`raffle-cancelled-${e.id}`} title={`Cancelled ${new Date(e.cancelled_at).toLocaleString()}`} className="text-xs text-red-700">Cancelled</span> : e.payment_review ? <span data-testid={`raffle-payment-review-${e.id}`} title={e.payment_review_reason} className="text-xs text-red-700">Payment review required</span> : e.finalization_pending ? <span data-testid={`raffle-payment-pending-${e.id}`} className="text-xs text-amber-800">Accounting pending</span> : e.paid
                                                            ? <span className="text-[10px] uppercase tracking-wider font-bold bg-green-100 text-green-800 px-2 py-0.5 rounded-full">Paid</span>
                                                            : <span className="text-[10px] uppercase tracking-wider font-bold bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full">Pending</span>
                                                        }
                                                    </td>
                                                    <td className="py-2 pr-3 text-xs text-[#4A5568] font-mono">{(e.ticket_numbers || []).join(", ") || "—"}</td>
                                                    <td className="py-2 pr-3">
                                                        <div className="flex items-center gap-1 flex-wrap">
                                                            {(!e.paid || e.finalization_pending) && !e.payment_review && !e.cancelled_at && (
                                                                <button onClick={() => markPaid(e)} data-testid={`raffle-pay-${e.id}`} className="inline-flex items-center gap-1 text-xs bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-full px-3 py-1 font-medium">
                                                                    <DollarSign className="w-3 h-3" strokeWidth={2} /> {e.finalization_pending ? "Retry record" : "Mark paid"}
                                                                </button>
                                                            )}
                                                            {e.paid && (e.ticket_numbers || []).length > 0 && (
                                                                <button
                                                                    onClick={() => downloadTicketPdf(e)}
                                                                    data-testid={`raffle-ticket-pdf-${e.id}`}
                                                                    className="inline-flex items-center gap-1 text-xs bg-white hover:bg-[#F4F1EA] text-[#1C252E] border border-black/15 rounded-full px-3 py-1 font-medium"
                                                                    title="Download printable ticket PDF with prize photo"
                                                                >
                                                                    <FileText className="w-3 h-3" strokeWidth={2} /> Ticket PDF
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default RafflesPanel;
